import React, { useState, useEffect } from "react";
import {
  ChevronDown,
  ChevronRight,
  User,
  Calendar,
  MapPin,
  Phone,
  Mail,
  Activity,
  CheckCircle,
  Clock,
  FileText,
  Loader2,
  AlertCircle,
  RefreshCw,
  ArrowRight,
} from "lucide-react";
import axios from "axios";
import { ATTENDANCE_REPORT_ZM } from "../../constants";
import { Dialog, DialogContent, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const BDMActionsReport = () => {
  const [expandedRegions, setExpandedRegions] = useState(new Set());
  const [expandedBDMs, setExpandedBDMs] = useState(new Set());
  const [attendanceData, setAttendanceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );

  const fetchReportData = async (date) => {
    try {
      setLoading(true);
      setError(null);
      const formattedStartDate = new Date(date).toISOString().split("T")[0]; // Simple date formatting

      // Simulating API call with setTimeout for demo purposes
      // Replace this with your actual axios call:

      const response = await axios.get(
        `${ATTENDANCE_REPORT_ZM}?employeeId=${localStorage.getItem(
          "username"
        )}&startDate=${formattedStartDate}&endDate=${formattedStartDate}`
      );
      setAttendanceData(response.data);
      setLoading(false);
      setError(null);

      // Mock response for demo - remove this when implementing real API
    } catch (err) {
      console.error("Error fetching data:", err);
      setError(err.message || "Failed to fetch data");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData(selectedDate);
  }, [selectedDate]);

  const toggleRegion = (regionId) => {
    const newExpanded = new Set(expandedRegions);
    if (newExpanded.has(regionId)) {
      newExpanded.delete(regionId);
    } else {
      newExpanded.add(regionId);
    }
    setExpandedRegions(newExpanded);
  };

  const toggleBDM = (bdmId) => {
    const newExpanded = new Set(expandedBDMs);
    if (newExpanded.has(bdmId)) {
      newExpanded.delete(bdmId);
    } else {
      newExpanded.add(bdmId);
    }
    setExpandedBDMs(newExpanded);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusColor = (status) => {
    return status === "completed" ? "text-green-600" : "text-orange-600";
  };

  // State to handle dialog
  const [openImageDialog, setOpenImageDialog] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const getTaskTypeColor = (taskType) => {
    const colors = {
      meeting: "bg-blue-100 text-blue-800",
      HO_task: "bg-purple-100 text-purple-800",
      other_task: "bg-green-100 text-green-800",
      self_task: "bg-orange-100 text-orange-800",
      ro_visit: "bg-indigo-100 text-indigo-800",
    };
    return colors[taskType] || "bg-gray-100 text-gray-800";
  };

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };

  const handleRefresh = () => {
    fetchReportData(selectedDate);
  };

  const DetailRow = ({ label, value }) => (
    <div className="flex justify-between items-center py-2 border-b border-gray-200">
      <span className="text-sm font-medium">{label}:</span>
      <span className="text-sm break-words capitalize">{value || "-"}</span>
    </div>
  );

  const VisitImageSection = ({
    mandatoryImage,
    optionalImage,
    onImageClick,
    setOpenImageDialog,
  }) => (
    <div className="mt-3 space-y-3">
      <ImageButton
        label="Visit Image"
        imageUrl={mandatoryImage}
        onImageClick={onImageClick}
        setOpenImageDialog={setOpenImageDialog}
      />
      <ImageButton
        label="Optional Visit Image"
        imageUrl={optionalImage}
        onImageClick={onImageClick}
        setOpenImageDialog={setOpenImageDialog}
      />
    </div>
  );

  const ImageButton = ({
    label,
    imageUrl,
    onImageClick,
    setOpenImageDialog,
  }) => (
    <div className="flex justify-between items-center">
      <span className="text-sm font-medium">{label}:</span>
      {imageUrl ? (
        <button
          className="flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-blue-500 px-3 py-1 rounded transition-colors"
          onClick={() => {
            onImageClick(imageUrl);
            setOpenImageDialog(true);
          }}
        >
          <span className="ml-1 text-xs">View</span>
          <ArrowRight className="h-3 w-3 ml-1" />
        </button>
      ) : (
        <span className="text-sm text-gray-500">No image</span>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Loading Report Data
          </h2>
          <p className="text-gray-600">
            Please wait while we fetch the latest information...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 text-center max-w-md">
          <AlertCircle className="w-8 h-8 text-red-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Error Loading Data
          </h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={handleRefresh}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center mx-auto"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!attendanceData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            No Data Available
          </h2>
          <p className="text-gray-600">
            Please select a date to view the report.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-6">
            {/* Header with title and controls */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                BDM Task Report
              </h1>

              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                {/* Date Picker */}
                <div className="flex items-center space-x-2">
                  <label
                    htmlFor="date-picker"
                    className="text-sm font-medium text-gray-700 whitespace-nowrap"
                  >
                    Select Date:
                  </label>
                  <input
                    id="date-picker"
                    type="date"
                    value={selectedDate}
                    onChange={handleDateChange}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Refresh Button */}
                <button
                  onClick={handleRefresh}
                  disabled={loading}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center justify-center"
                >
                  <RefreshCw
                    className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`}
                  />
                  Refresh
                </button>

                {/* Date Range Display */}
                <div className="text-sm text-gray-600 whitespace-nowrap">
                  <Calendar className="inline w-4 h-4 mr-1" />
                  {formatDate(attendanceData.dateRange.startDate)} -{" "}
                  {formatDate(attendanceData.dateRange.endDate)}
                </div>
              </div>
            </div>

            {/* Zonal Manager Info */}
            <div className="bg-blue-50 rounded-lg p-4 mb-4">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <div className="flex items-center text-blue-900">
                  <User className="w-5 h-5 text-blue-600 mr-2" />
                  <span className="font-semibold">Zonal Manager:</span>
                </div>
                <span className="text-blue-800 sm:ml-2">
                  {attendanceData.employeeInfo.EmployeeName} (ID:{" "}
                  {attendanceData.employeeInfo.EmployeeId})
                </span>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-4 text-white">
                <div className="text-2xl font-bold">
                  {attendanceData.overallSummary.totalRegions}
                </div>
                <div className="text-blue-100">Total Regions</div>
              </div>
              <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-4 text-white">
                <div className="text-2xl font-bold">
                  {attendanceData.overallSummary.totalBdms}
                </div>
                <div className="text-green-100">Total BDMs</div>
              </div>
              <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-4 text-white">
                <div className="text-2xl font-bold">
                  {attendanceData.overallSummary.totalActivities}
                </div>
                <div className="text-purple-100">Total Activities</div>
              </div>
              <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg p-4 text-white">
                <div className="text-2xl font-bold">
                  {attendanceData.overallSummary.meetings}
                </div>
                <div className="text-orange-100">Meetings</div>
              </div>
            </div>
          </div>

          {/* Regions List */}
          <div className="space-y-4">
            {attendanceData.regions.map((region, index) => (
              <div
                key={region.RegionId}
                className="bg-white rounded-xl shadow-lg overflow-hidden"
              >
                {/* Region Header */}
                <div
                  className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white p-4 sm:p-6 cursor-pointer hover:from-indigo-600 hover:to-purple-700 transition-all duration-200 rounded-lg"
                  onClick={() => toggleRegion(region.RegionId)}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    {/* Left side: Icon + Name + Project */}
                    <div className="flex items-start sm:items-center space-x-3">
                      {expandedRegions.has(region.RegionId) ? (
                        <ChevronDown className="w-5 h-5 mt-1 sm:mt-0" />
                      ) : (
                        <ChevronRight className="w-5 h-5 mt-1 sm:mt-0" />
                      )}
                      <MapPin className="w-5 h-5 mt-1 sm:mt-0" />
                      <div>
                        <h2 className="text-lg sm:text-xl font-bold">
                          {region.RegionName}
                        </h2>
                        <p className="text-indigo-100 text-sm truncate">
                          Project: {region.Project}
                        </p>
                      </div>
                    </div>

                    {/* Right side: Summary badges */}
                    <div className="flex flex-wrap sm:flex-nowrap gap-2 text-sm sm:text-base">
                      <span className="bg-[rgba(255,255,255,0.2)] px-3 py-1 rounded">
                        {region.summary.totalBdms} BDMs
                      </span>
                      <span className="bg-[rgba(255,255,255,0.2)] px-3 py-1 rounded">
                        {region.summary.totalActivities} Activities
                      </span>
                    </div>
                  </div>
                </div>

                {/* Region Content */}
                {expandedRegions.has(region.RegionId) && (
                  <div className="p-6">
                    {/* Region Summary */}
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
                      <div className="bg-blue-50 p-3 rounded-lg text-center">
                        <div className="text-lg font-bold text-blue-600">
                          {region.summary.meetings}
                        </div>
                        <div className="text-xs text-blue-500">Meetings</div>
                      </div>
                      <div className="bg-green-50 p-3 rounded-lg text-center">
                        <div className="text-lg font-bold text-green-600">
                          {region.summary.roVisits}
                        </div>
                        <div className="text-xs text-green-500">RO Visits</div>
                      </div>
                      <div className="bg-purple-50 p-3 rounded-lg text-center">
                        <div className="text-lg font-bold text-purple-600">
                          {region.summary.hoVisits}
                        </div>
                        <div className="text-xs text-purple-500">HO Visits</div>
                      </div>
                      <div className="bg-orange-50 p-3 rounded-lg text-center">
                        <div className="text-lg font-bold text-orange-600">
                          {region.summary.boVisits}
                        </div>
                        <div className="text-xs text-orange-500">BO Visits</div>
                      </div>
                      <div className="bg-indigo-50 p-3 rounded-lg text-center">
                        <div className="text-lg font-bold text-indigo-600">
                          {region.summary.travels}
                        </div>
                        <div className="text-xs text-indigo-500">Travels</div>
                      </div>
                    </div>

                    {/* BDMs */}
                    <div className="space-y-4">
                      {region.bdms.map((bdm) => (
                        <div
                          key={bdm.EmployeeId}
                          className="border border-gray-200 rounded-lg overflow-hidden"
                        >
                          {/* BDM Header */}
                          <div
                            className="bg-gray-50 p-4 cursor-pointer hover:bg-gray-100 transition-colors duration-200"
                            onClick={() => toggleBDM(bdm.EmployeeId)}
                          >
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                              {/* Left section */}
                              <div className="flex items-start sm:items-center space-x-3">
                                {expandedBDMs.has(bdm.EmployeeId) ? (
                                  <ChevronDown className="w-4 h-4 text-gray-600 mt-1 sm:mt-0" />
                                ) : (
                                  <ChevronRight className="w-4 h-4 text-gray-600 mt-1 sm:mt-0" />
                                )}
                                <User className="w-5 h-5 text-blue-600" />
                                <div>
                                  <h3 className="font-bold text-gray-900 text-base sm:text-lg">
                                    {bdm.EmployeeName}
                                  </h3>
                                  <p className="text-sm text-gray-600">
                                    Employee ID: {bdm.EmployeeId}
                                  </p>
                                </div>
                              </div>

                              {/* Right section */}
                              <div className="flex flex-wrap gap-2 sm:gap-4">
                                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap">
                                  <Activity className="w-4 h-4 inline mr-1" />
                                  {bdm.actionCount} Actions
                                </span>
                                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap">
                                  BDM
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* BDM Actions */}
                          {expandedBDMs.has(bdm.EmployeeId) && (
                            <div className="p-2 bg-white">
                              {/* BDM Activity Summary */}
                              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-4">
                                <div className="bg-blue-50 p-2 rounded text-center">
                                  <div className="font-bold text-blue-600">
                                    {bdm.activitySummary.meetings}
                                  </div>
                                  <div className="text-xs text-blue-500">
                                    Meetings
                                  </div>
                                </div>
                                <div className="bg-green-50 p-2 rounded text-center">
                                  <div className="font-bold text-green-600">
                                    {bdm.activitySummary.roVisits}
                                  </div>
                                  <div className="text-xs text-green-500">
                                    RO Visits
                                  </div>
                                </div>
                                <div className="bg-purple-50 p-2 rounded text-center">
                                  <div className="font-bold text-purple-600">
                                    {bdm.activitySummary.hoVisits}
                                  </div>
                                  <div className="text-xs text-purple-500">
                                    HO Visits
                                  </div>
                                </div>
                                <div className="bg-orange-50 p-2 rounded text-center">
                                  <div className="font-bold text-orange-600">
                                    {bdm.activitySummary.boVisits}
                                  </div>
                                  <div className="text-xs text-orange-500">
                                    BO Visits
                                  </div>
                                </div>
                                <div className="bg-indigo-50 p-2 rounded text-center">
                                  <div className="font-bold text-indigo-600">
                                    {bdm.activitySummary.travels}
                                  </div>
                                  <div className="text-xs text-indigo-500">
                                    Travels
                                  </div>
                                </div>
                                <div className="bg-teal-50 p-2 rounded text-center">
                                  <div className="font-bold text-teal-600">
                                    {bdm.activitySummary.siteVisits}
                                  </div>
                                  <div className="text-xs text-teal-500">
                                    Site Visits
                                  </div>
                                </div>
                              </div>

                              {/* Actions List */}
                              <div className="space-y-3">
                                <h4 className="font-semibold text-gray-900 border-b pb-2">
                                  Detailed Actions
                                </h4>
                                {bdm.actions.map((action) => (
                                  <div
                                    key={action.id}
                                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200"
                                  >
                                    <div className="flex items-start justify-between mb-3">
                                      <div className="flex items-center space-x-3">
                                        <div
                                          className={`px-3 py-1 rounded-full text-xs font-medium ${getTaskTypeColor(
                                            action.task_type
                                          )}`}
                                        >
                                          {action.task_type
                                            .replace("_", " ")
                                            .toUpperCase()}
                                        </div>
                                        <span
                                          className={`flex items-center text-sm font-medium ${getStatusColor(
                                            action.completion_status
                                          )}`}
                                        >
                                          {action.completion_status ===
                                          "completed" ? (
                                            <CheckCircle className="w-4 h-4 mr-1" />
                                          ) : (
                                            <Clock className="w-4 h-4 mr-1" />
                                          )}
                                          {action.completion_status ||
                                            "Pending"}
                                        </span>
                                      </div>
                                    </div>
                                    <div className="text-right text-sm text-gray-600 mb-1">
                                      <div className="flex items-center">
                                        <Calendar className="w-4 h-4 mr-1" />
                                        {formatDate(action.action_date)}
                                      </div>
                                    </div>

                                    <div className="grid md:grid-cols-2 gap-4">
                                      {/* Task Details */}
                                      <div>
                                        <h5 className="font-semibold text-gray-900 mb-2 flex items-center">
                                          <FileText className="w-4 h-4 mr-1" />
                                          Task Details
                                        </h5>
                                        <div className="space-y-1 text-sm">
                                          <div>
                                            <span className="text-gray-600">
                                              Task Name:
                                            </span>{" "}
                                            <span className="font-medium">
                                              {action.task_name}
                                            </span>
                                          </div>

                                          <div>
                                            <span className="text-gray-600">
                                              Action Type:
                                            </span>{" "}
                                            <span className="font-medium">
                                              {action.action_type}
                                            </span>
                                          </div> 
                                          </div>
                                        
                                          
                                      </div>
                                      
                                      <div className="space-y-1">
                                          {/* BO / RO Visit Section */}
                                          {(action.specific_action ===
                                            "BO Visit" ||
                                            action.specific_action ===
                                              "RO Visit") && (
                                            <>
                                              {action.specific_action ===
                                                "RO Visit" && (
                                                <DetailRow
                                                  label="Regional Office"
                                                  value={
                                                    action?.travel_form_details
                                                      ?.regionalOfficeName
                                                  }
                                                />
                                              )}
                                              {action.specific_action ===
                                                "BO Visit" && (
                                                <DetailRow
                                                  label="Branch Office"
                                                  value={
                                                    action?.travel_form_details
                                                      ?.branchName
                                                  }
                                                />
                                              )}
                                              <DetailRow
                                                label="Purpose"
                                                value={
                                                  action?.travel_form_details
                                                    ?.purposeForVisit
                                                }
                                              />

                                              {action?.travel_form_details
                                                ?.purposeForVisit ===
                                                "Meeting_with_RH_BM_ZH" && (
                                                <DetailRow
                                                  label="Concern Person"
                                                  value={
                                                    action?.travel_form_details
                                                      ?.concernPersonName
                                                  }
                                                />
                                              )}
                                              {action?.travel_form_details
                                                ?.purposeForVisit ===
                                                "admin_work" && (
                                                <DetailRow
                                                  label="Admin Task"
                                                  value={
                                                    action?.travel_form_details
                                                      ?.adminTaskSelect
                                                  }
                                                />
                                              )}

                                              <DetailRow
                                                label="Remarks"
                                                value={
                                                  action?.travel_form_details
                                                    ?.remarks
                                                }
                                              />

                                              {/* Visit Images */}
                                              <VisitImageSection
                                                mandatoryImage={
                                                  action?.travel_form_details
                                                    ?.mandatoryVisitImage
                                                }
                                                optionalImage={
                                                  action?.travel_form_details
                                                    ?.optionalVisitImage
                                                }
                                                onImageClick={setSelectedImage}
                                                setOpenImageDialog={
                                                  setOpenImageDialog
                                                }
                                              />
                                            </>
                                          )}

                                          {/* HO Visit Section */}
                                          {action.specific_action ===
                                            "HO Visit" && (
                                            <div className="mb-4 animate-fadeIn">
                                              <div className=" p-3 rounded-lg shadow-sm hover:shadow-md transition-shadow space-y-2">
                                                <DetailRow
                                                  label="Purpose"
                                                  value={
                                                    action?.travel_form_details
                                                      ?.hoSelection
                                                  }
                                                />
                                                <DetailRow
                                                  label="Remarks"
                                                  value={
                                                    action?.travel_form_details
                                                      ?.remarks
                                                  }
                                                />

                                                <VisitImageSection
                                                  mandatoryImage={
                                                    action?.travel_form_details
                                                      ?.mandatoryVisitImage
                                                  }
                                                  optionalImage={
                                                    action?.travel_form_details
                                                      ?.optionalVisitImage
                                                  }
                                                  onImageClick={
                                                    setSelectedImage
                                                  }
                                                  setOpenImageDialog={
                                                    setOpenImageDialog
                                                  }
                                                />
                                              </div>
                                            </div>
                                          )}

                                          {/* Customer Details */}
                                      {action.Lead && (
                                        <div>
                                          <h5 className="font-semibold text-gray-900 mb-2 flex items-center">
                                            <User className="w-4 h-4 mr-1" />
                                            Customer Details
                                          </h5>
                                          <div className=" text-sm">
                                            <DetailRow
                                              label="Name"
                                              value={action.Lead.CustomerName}
                                            />
                                           <DetailRow
                                              label="Mobile Number"
                                              value={action.Lead.MobileNo}
                                            />
                                            {action.Lead.CustomerMailId && (
                                              <div className="flex items-center">
                                                <Mail className="w-3 h-3 mr-1 text-gray-400" />
                                                <span className="font-medium">
                                                  {action.Lead.CustomerMailId}
                                                </span>
                                              </div>
                                            )}
                                            <DetailRow
                                              label="Category"
                                              value={action.Lead.category}
                                            />
                                            <DetailRow
                                              label="Sub-Category"
                                              value={action.Lead.sub_category}
                                            />
                                            <DetailRow
                                              label="Remarks"
                                              value={action.Lead.bdm_remark}
                                            />
                                          </div>
                                        </div>
                                      )}
                                        </div>

                                      
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <Dialog
        open={openImageDialog}
        onClose={() => setOpenImageDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent
          sx={{ p: 0, position: "relative", backgroundColor: "black" }}
        >
          {/* Close Button */}
          <IconButton
            aria-label="close"
            onClick={() => {
              setOpenImageDialog(false);
              setSelectedImage(null);
            }}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "#fff",
              zIndex: 1,
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Image */}
          <img
            src={selectedImage}
            alt="Visit"
            style={{ width: "100%", height: "auto", display: "block" }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BDMActionsReport;
